import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_DATA_TRANSFORM_LANGUAGE } from '@gql/talentLanguages';
import { fetchLanguageOptions } from '../utils/languageUtils';
import { TalentLanguage, LanguageOption, LevelOption } from '../types';

interface UseLanguageOptionsResult {
    languageOptions: LanguageOption[];
    levelOptions: LevelOption[];
}

/**
 * Provides available language and proficiency level options for the form selects.
 * - Language options are fetched with a localStorage cache (1-hour TTL).
 * - Level options are fetched fresh via GraphQL on mount.
 */
export const useLanguageOptions = (selectedData: TalentLanguage | null): UseLanguageOptionsResult => {
    const [levelOptions, setLevelOptions] = useState<LevelOption[]>([]);
    const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>(
        (selectedData?.languages?.[0]?.language ?? []).map(item => ({
            label: item.name,
            value: item._id,
            code: item.code,
        })),
    );

    const [fetchLevelOptions] = useLazyQuery(GET_DATA_TRANSFORM_LANGUAGE, {
        onCompleted: result => {
            const levels = result?.getDataTransformLanguage?.levelLanguage ?? [];
            setLevelOptions(
                levels.map((item: { level: string; _id: string }) => ({
                    label: item.level,
                    value: item._id,
                })),
            );
        },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        fetchLevelOptions();
    }, [fetchLevelOptions]);

    useEffect(() => {
        fetchLanguageOptions()
            .then(options => setLanguageOptions(options))
            .catch(err => console.error('Failed to load language options:', err));
    }, []);

    return { languageOptions, levelOptions };
};
